/* ═══════════════════════════════════════════════════════════════════════
   THE ESSENCE — premium collectible card gallery (zero-cost)
   • Every memory image the user has ever uploaded is presented as a
     dark-luxury collectible trading card (black + gold "Old Money" theme).
   • Cards are swipeable horizontally on mobile, scrollable on desktop.
   • All stats are derived deterministically from the memory metadata —
     no backend, no AI, no cloud cost.

   Stat rules (per the user's brief — adapted to zero-cost heuristics):
     • Strength    — derived from category × person/accessory weight
     • Resilience  — total uploads in the user's vault
     • Power       — derived from category (living things / accessories)
     • Wisdom      — cumulative app session time (tracked client-side)
     • Rank        — Initiate / Rising / Master / Ascendant from sum
   ───────────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { BackPill } from './BackPill';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BACKEND_URL } from '../../utils/api';
import { SignaturePad } from './SignaturePad';
import { hiResAvatar } from '../../utils/avatar';

const GOLD       = '#D4AF37';
const GOLD_HI    = '#F5D97A';
const GOLD_DEEP  = '#8B6B1F';
const INK        = '#0c0a08';

/* ── Deterministic 0..n integer hash from any string ── */
const hashInt = (str, mod = 100) => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) - h) + str.charCodeAt(i);
    h |= 0;  // 32-bit
  }
  return Math.abs(h) % mod;
};

/* ── Category → power profile (zero-cost heuristic) ── */
const CATEGORY_POWER = {
  fashion: { strength: 95, power: 85 },
  food:    { strength: 75, power: 95 },
  explore: { strength: 88, power: 92 },
  souls:   { strength: 92, power: 98 },
  sporty:  { strength: 96, power: 88 },
  success: { strength: 90, power: 90 },
  legacy:  { strength: 85, power: 88 },
  art:     { strength: 80, power: 95 },
};

const RANK_TIERS = [
  { min: 92, tier: 'ASCENDANT', subtitle: 'Ascendant' },
  { min: 86, tier: 'MASTER',    subtitle: 'Master'    },
  { min: 78, tier: 'RISING',    subtitle: 'Rising'    },
  { min: 0,  tier: 'INITIATE',  subtitle: 'Initiate'  },
];

const computeRank = (sum) => {
  const avg = sum / 4;
  return RANK_TIERS.find((t) => avg >= t.min) || RANK_TIERS[RANK_TIERS.length - 1];
};

/* ── Category → animal/figure icon + RICH JEWELED palette ──
   Each `bg` is a saturated, gem-tone (emerald, sapphire, ruby, amethyst,
   citrine) that stays elegant against the obsidian app background but
   reads clearly at arm's length. `accent` is the metallic figure glow. */
const CATEGORY_META = {
  fashion: { icon: '/icons/leopard_v2.png',    accent: '#F5D97A', bg: '#6b3e18', label: 'Fashion' },  // burnished amber
  food:    { icon: '/icons/gazelle.png',       accent: '#FFDF7A', bg: '#5a4416', label: 'Taste'   },  // deep honey
  explore: { icon: '/icons/falcon_v2.png',     accent: '#8FB4FF', bg: '#183a7a', label: 'Explore' },  // vivid sapphire
  souls:   { icon: '/icons/phoenix_v2.png',    accent: '#FF7A8A', bg: '#7a1428', label: 'Soul'    },  // brilliant ruby
  sporty:  { icon: '/icons/gorilla_v2.png',    accent: '#3DDC97', bg: '#0f5236', label: 'Sport'   },  // rich emerald
  success: { icon: '/icons/stallion_v2.png',   accent: '#F0F0F0', bg: '#3a3a44', label: 'Success' },  // polished pewter
  legacy:  { icon: '/icons/lion_legacy.png',   accent: '#C39CFF', bg: '#3f1f66', label: 'Legacy'  },  // royal amethyst
  art:     { icon: '/icons/crystal_crane.png', accent: '#5FAAFF', bg: '#173561', label: 'Art'     },  // cobalt blue
};

/* The 8 master cards drive the new pillar overview screen. Same id set
   as CATEGORY_META so a tap on one master drills into that pillar's
   essence cards. */
const PILLAR_ORDER = ['fashion', 'food', 'explore', 'souls', 'sporty', 'success', 'legacy', 'art'];

/* ── Card title: poetic per-category default with optional user note ── */
const CATEGORY_TITLE = {
  fashion: 'The Atelier Ally',
  food:    'The Taste Curator',
  explore: 'The Wayfarer',
  souls:   'The Soul Keeper',
  sporty:  'The Iron Heart',
  success: 'The Conquest Bearer',
  legacy:  'The Lineage Holder',
  art:     'The Aesthete',
};

/* ── Inline stat-icon SVGs (zero-cost, on-aesthetic) ── */
const IconFlex = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={GOLD_HI} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 14c2-1.8 3-3.5 3-6.5C9 5 7.5 4 6 4"/>
    <path d="M9 8c2.4 0 4 1.5 4 4 0 1.4-.6 2.6-1.6 3.4l2.2 1.4c1.4-1.2 2.4-3 2.4-5C16 8.5 13.8 6 11 6"/>
    <path d="M11.5 15.8c-1 .8-2.5 1.2-4 1.2H4l1.2 2.5C6 21 7.7 22 9.5 22h2c2.8 0 4.5-1.7 4.5-4 0-1.1-.4-2-1-2.6"/>
  </svg>
);
const IconShield = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={GOLD_HI} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5l-8-3Z"/>
  </svg>
);
const IconBolt = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={GOLD_HI} stroke={GOLD_HI} strokeWidth="0.6" strokeLinejoin="round" aria-hidden>
    <path d="M13 2 4 13h6l-2 9 10-13h-6l1-7Z"/>
  </svg>
);
const IconBook = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={GOLD_HI} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4 4h6a3 3 0 0 1 3 3v14a2 2 0 0 0-2-2H4Z"/>
    <path d="M20 4h-6a3 3 0 0 0-3 3v14a2 2 0 0 1 2-2h7Z"/>
  </svg>
);
const IconStar = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={GOLD_HI} stroke={GOLD_HI} strokeWidth="0.5" strokeLinejoin="round" aria-hidden>
    <path d="M12 2 14.6 8.5 21.5 9.2 16.3 13.8 17.9 20.6 12 17 6.1 20.6 7.7 13.8 2.5 9.2 9.4 8.5Z"/>
  </svg>
);

/* ── Stat block (label + value with icon) ── */
const StatBlock = ({ Icon, label, value }) => (
  <div className="flex items-center gap-2">
    <Icon size={20}/>
    <div className="leading-tight">
      <div className="font-['Cormorant_Garamond'] uppercase tracking-[0.16em]"
        style={{ color: 'rgba(245,217,122,0.65)', fontSize: 9 }}>
        {label}
      </div>
      <div className="font-['Cormorant_Garamond'] font-semibold"
        style={{
          color: GOLD_HI, fontSize: 22, letterSpacing: '0.04em',
          textShadow: `0 0 8px ${GOLD}44`,
          lineHeight: 1,
        }}>
        {value}
      </div>
    </div>
  </div>
);

/* ── Filigree corner ornament (gold scrollwork SVG) ── */
const FiligreeCorner = ({ rotation = 0, size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden
    style={{ transform: `rotate(${rotation}deg)` }}>
    <path
      d="M2 2 Q14 2 16 12 Q18 4 30 4"
      fill="none" stroke={GOLD} strokeWidth="0.9" strokeLinecap="round"/>
    <path
      d="M2 8 Q10 8 12 14"
      fill="none" stroke={GOLD} strokeWidth="0.7"/>
    <circle cx="16.5" cy="11.5" r="1.4" fill={GOLD_HI} stroke={GOLD_DEEP} strokeWidth="0.3"/>
  </svg>
);

/* ══════════════════════════ ESSENCE CARD ══════════════════════════ */
export const EssenceCard = ({ memory, user, totalUploads, sessionMinutes, master = false }) => {
  const seedKey = memory.id || memory.image_url || memory.created_at || 'seed';
  const category = (memory.category || 'art').toLowerCase();
  const catProfile = CATEGORY_POWER[category] || CATEGORY_POWER.art;

  /* Per-card deterministic variation (so each card has unique-looking stats) */
  const hVar = hashInt(seedKey, 8);  // 0..7 spread

  /* Stats */
  const strength = Math.max(60, Math.min(99, catProfile.strength - 3 + hVar));
  const power    = Math.max(60, Math.min(99, catProfile.power    - 4 + ((hVar + 3) % 8)));
  /* Resilience = global uploads, capped at 99 (matches user's brief). */
  const resilience = Math.max(1, Math.min(99, totalUploads));
  /* Wisdom = cumulative minutes spent in app (capped 0..99). */
  const wisdom = Math.max(0, Math.min(99, Math.round(sessionMinutes)));

  const rankInfo = computeRank(strength + resilience + power + wisdom);

  const displayName = (user?.nickname || user?.email?.split('@')[0] || 'Sovereign').toUpperCase();
  /* Per-category meta — drives the card's accent color & icon. */
  const meta = CATEGORY_META[category] || CATEGORY_META.art;
  const categoryName = meta.label;
  const catIcon = meta.icon;
  const ACCENT = meta.accent;
  const BG_DEEP = meta.bg;

  return (
    <div
      className="relative flex-shrink-0 mx-auto flex flex-col"
      style={{
        height: 'min(100%, 660px)',
        aspectRatio: '9 / 16',
        maxWidth: '82vw',
        background:
          /* Layer 1 — leather grain noise */
          'repeating-linear-gradient(45deg, rgba(255,255,255,0.012) 0px, rgba(255,255,255,0.012) 1px, transparent 1px, transparent 3px),' +
          /* Layer 2 — accent-color wash from top (stronger, elite jewel tone) */
          `radial-gradient(ellipse at 50% -10%, ${ACCENT}66 0%, transparent 55%),` +
          /* Layer 3 — jewel bg holds farther before fading to obsidian core */
          `radial-gradient(ellipse at 50% 45%, ${BG_DEEP} 0%, ${BG_DEEP}cc 30%, #0a0805 75%, #050304 100%)`,
        borderRadius: 22,
        /* Beveled gold border */
        border: `2.5px solid ${GOLD}`,
        boxShadow:
          `0 0 0 1.5px ${GOLD_DEEP} inset,` +              /* inner gold pin */
          `inset 0 1px 0 ${GOLD_HI}55,` +                  /* top bevel highlight */
          `inset 0 -1px 0 rgba(0,0,0,0.6),` +              /* bottom bevel shadow */
          `inset 0 0 70px rgba(0,0,0,0.55),` +
          `0 0 24px ${ACCENT}55,` +                        /* accent aura */
          `0 18px 40px rgba(0,0,0,0.75)`,
        padding: '14px 16px 14px',
        overflow: 'hidden',
      }}
      data-testid={`essence-card-${memory.id}`}
    >
      {/* Inner thin gold border */}
      <div aria-hidden className="absolute pointer-events-none" style={{
        top: 7, left: 7, right: 7, bottom: 7,
        border: `1px solid ${GOLD}66`, borderRadius: 18,
      }}/>

      {/* Embossed leather diagonal corner etchings (4 corners) */}
      {[
        { top: 10, left: 10, rotate: 0   },
        { top: 10, right: 10, rotate: 90 },
        { bottom: 10, right: 10, rotate: 180 },
        { bottom: 10, left: 10, rotate: 270 },
      ].map((p, i) => (
        <svg key={i} width="32" height="32" viewBox="0 0 32 32" aria-hidden
          className="absolute pointer-events-none"
          style={{ ...p, transform: `rotate(${p.rotate}deg)` }}>
          <path d="M2 4 L14 16 M2 8 L10 16 M2 12 L6 16"
            fill="none" stroke={GOLD_HI} strokeWidth="0.7" opacity="0.45"/>
        </svg>
      ))}

      {/* Header band — "BLISS COLLECTION" */}
      <div className="relative flex items-center justify-center pb-2 flex-shrink-0">
        <Sparkles size={11} style={{ color: GOLD, marginRight: 6 }}/>
        <p className="font-['Cormorant_Garamond'] font-semibold uppercase"
          style={{ color: GOLD_HI, fontSize: 11, letterSpacing: '0.34em',
            textShadow: `0 0 10px ${ACCENT}aa` }}>
          BLISS COLLECTION
        </p>
        <Sparkles size={11} style={{ color: GOLD, marginLeft: 6 }}/>
      </div>

      {/* Portrait — fills all available vertical space (flex-grow).
          In master mode the portrait gets significantly more height
          because the stats grid below is hidden. */}
      <div className="relative mx-auto flex-1 min-h-0" style={{ width: master ? '88%' : '82%' }}>
        {/* Filigree side flourishes */}
        <div className="absolute pointer-events-none" style={{ left: -14, top: '38%', transform: 'translateY(-50%)' }}>
          <svg width="18" height="36" viewBox="0 0 20 36" aria-hidden>
            <path d="M19 0 Q5 8 10 18 Q15 28 19 36" fill="none" stroke={GOLD} strokeWidth="0.9"/>
            <circle cx="10" cy="18" r="1.6" fill={GOLD_HI}/>
          </svg>
        </div>
        <div className="absolute pointer-events-none" style={{ right: -14, top: '38%', transform: 'translateY(-50%) scaleX(-1)' }}>
          <svg width="18" height="36" viewBox="0 0 20 36" aria-hidden>
            <path d="M19 0 Q5 8 10 18 Q15 28 19 36" fill="none" stroke={GOLD} strokeWidth="0.9"/>
            <circle cx="10" cy="18" r="1.6" fill={GOLD_HI}/>
          </svg>
        </div>

        {/* Frame */}
        <div className="absolute inset-0 overflow-hidden" style={{
          border: `2px solid ${GOLD}`,
          borderRadius: 7,
          boxShadow: `inset 0 0 0 1px ${GOLD_HI}77, 0 4px 14px rgba(0,0,0,0.6)`,
          background: '#0d0820',
        }}>
          {memory.image_url ? (
            <img src={memory.image_url} alt={categoryName} draggable={false}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
          ) : (
            <div className="w-full h-full flex items-center justify-center font-['Cormorant_Garamond']"
              style={{ color: GOLD, fontSize: 42, fontStyle: 'italic' }}>
              {(user?.nickname || 'A')[0]?.toUpperCase() || 'A'}
            </div>
          )}
        </div>
        {/* Corner brackets */}
        {[
          { top: -3, left: -3, rot: 0 },
          { top: -3, right: -3, rot: 90 },
          { bottom: -3, right: -3, rot: 180 },
          { bottom: -3, left: -3, rot: 270 },
        ].map((p, i) => (
          <div key={i} className="absolute pointer-events-none" style={{
            top: p.top, left: p.left, right: p.right, bottom: p.bottom,
            transform: `rotate(${p.rot}deg)`,
          }}>
            <FiligreeCorner rotation={0} size={16}/>
          </div>
        ))}
      </div>

      {/* Username — italic gold, sits directly above category & medallion */}
      <h2 className="text-center font-['Cormorant_Garamond'] font-bold italic mt-3 leading-none flex-shrink-0"
        style={{
          fontSize: 'clamp(26px, 7.5vw, 34px)',
          letterSpacing: '0.08em',
          background: `linear-gradient(180deg, #FFF1B8 0%, ${GOLD_HI} 45%, ${GOLD_DEEP} 100%)`,
          WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
          textShadow: '0 1px 0 rgba(0,0,0,0.45)',
        }}>
        {displayName}
      </h2>

      {/* Category label */}
      <p className="text-center font-['Cormorant_Garamond'] uppercase tracking-[0.4em] mt-1 flex-shrink-0"
        style={{ color: GOLD_HI, fontSize: 11, textShadow: `0 0 8px ${GOLD}44` }}>
        {categoryName.toUpperCase()}
      </p>

      {/* Filigree divider — hidden on master cards (no stats below) */}
      {!master && (
      <div className="mt-1.5 mb-2 mx-auto flex items-center gap-2 w-full max-w-[70%] flex-shrink-0">
        <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}/>
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
          <path d="M5 0 L6.3 3.7 L10 5 L6.3 6.3 L5 10 L3.7 6.3 L0 5 L3.7 3.7 Z" fill={GOLD_HI}/>
        </svg>
        <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}/>
      </div>
      )}

      {/* Stats grid — hidden on master cards.  Master cards show only
          the portrait + ARSH name + category (per user spec). */}
      {!master && (
      <div className="grid grid-cols-3 items-center gap-x-2 gap-y-3 flex-shrink-0"
        style={{ paddingInline: 2 }}>
        {/* LEFT column — top: Strength */}
        <div className="flex justify-start">
          <StatBlock Icon={IconFlex} label="Strength" value={strength}/>
        </div>

        {/* CENTER column — medallion spans both rows */}
        <div className="row-span-2 flex flex-col items-center justify-center">
          <EssenceMedallion icon={catIcon} accent={ACCENT}/>
          <div className="font-['Cormorant_Garamond'] uppercase tracking-[0.18em] mt-1.5"
            style={{ color: 'rgba(245,217,122,0.6)', fontSize: 8.5 }}>
            Rank
          </div>
          <div className="font-['Cormorant_Garamond'] font-bold uppercase"
            style={{
              color: GOLD_HI, fontSize: 12, letterSpacing: '0.18em',
              textShadow: `0 0 8px ${GOLD}66`, lineHeight: 1,
            }}>
            {rankInfo.subtitle}
          </div>
        </div>

        {/* RIGHT column — top: Resilience */}
        <div className="flex justify-end">
          <StatBlock Icon={IconShield} label="Resilience" value={resilience}/>
        </div>

        {/* LEFT column — bottom: Power */}
        <div className="flex justify-start">
          <StatBlock Icon={IconBolt} label="Power" value={power}/>
        </div>

        {/* RIGHT column — bottom: Wisdom */}
        <div className="flex justify-end">
          <StatBlock Icon={IconBook} label="Wisdom" value={wisdom}/>
        </div>
      </div>
      )}
    </div>
  );
};

/* ── Premium centerpiece medallion — gold ring + crisp chapter icon ──
   Uses `mix-blend-mode: lighten` (lighter of source/dest wins per pixel)
   so the icon's solid-black PNG background "vanishes" into the dark
   medallion interior with NO blur and NO black square. */
const EssenceMedallion = ({ icon, accent = GOLD }) => (
  <div className="relative" style={{ width: 64, height: 64 }}>
    {/* Outer halo (accent-colored) */}
    <div aria-hidden className="absolute inset-0 rounded-full" style={{
      background: `radial-gradient(circle, ${accent}77 0%, transparent 70%)`,
      filter: 'blur(8px)', transform: 'scale(1.45)',
    }}/>
    {/* Gold outer ring */}
    <div className="absolute inset-0 rounded-full overflow-hidden" style={{
      border: `2px solid ${GOLD}`,
      boxShadow:
        `inset 0 0 0 1.5px ${GOLD_HI}88,` +
        `0 0 10px ${accent}cc,` +
        `0 4px 10px rgba(0,0,0,0.6)`,
      /* Solid near-black interior — gives `lighten` a true dark target. */
      background: '#050308',
    }}>
      {/* Inner gold pin-ring */}
      <div aria-hidden className="absolute pointer-events-none" style={{
        inset: 5, borderRadius: '50%',
        border: `1px solid ${GOLD}66`,
      }}/>
      {/* Icon — `lighten` blend drops the black PNG bg, NO blur filter */}
      <img src={icon} alt="" draggable={false}
        style={{
          width: '100%', height: '100%', objectFit: 'contain',
          padding: '4px',
          mixBlendMode: 'lighten',
        }}/>
    </div>
  </div>
);

/* ══════════════════════════ ESSENCE VAULT MODAL ══════════════════════════ */
export const EssenceVault = ({ onClose }) => {
  const { user, updateProfile } = useAuth();
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [direction, setDirection] = useState(1);
  /* When null → show the master card. When '__all__' → drilled swiper. */
  const [drillPillar, setDrillPillar] = useState(null);
  /* Top-level sub-tab inside Essence.
     Default 'stamp' — user opens Essence and sees their Bliss identity first. */
  const [tab, setTab] = useState('stamp');

  /* Track cumulative app session time (zero-cost, client-side only).
     Starts a tick on mount and adds to localStorage. */
  const [sessionMinutes, setSessionMinutes] = useState(0);
  useEffect(() => {
    const KEY = 'essence_session_minutes';
    const stored = parseFloat(localStorage.getItem(KEY) || '0');
    setSessionMinutes(stored);
    const t0 = Date.now();
    const interval = setInterval(() => {
      const delta = (Date.now() - t0) / 60000;  // minutes
      const next = stored + delta;
      setSessionMinutes(next);
      localStorage.setItem(KEY, String(next));
    }, 15000);  // every 15s
    return () => clearInterval(interval);
  }, []);

  /* Load all memories (the user's entire vault).
     We fetch per-category in parallel and aggregate because the bare
     /api/memories endpoint behaves inconsistently across deployments,
     while /api/memories?category=X is the same endpoint MemoryGrid uses
     in the Empire tab and is known to work in production. */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        PILLAR_ORDER.map((cat) =>
          axios.get(`${BACKEND_URL}/api/memories?category=${cat}`)
            .then((r) => Array.isArray(r.data) ? r.data : [])
            .catch(() => [])
        )
      );
      const flat = results.flat().filter((m) => m && m.image_url);
      setMemories(flat);
    } catch (_) {
      setMemories([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const total = memories.length;
  /* Memories to display in the swiper.
     - '__all__' → every memory sorted newest → oldest by upload date.
     - specific pillar id → filtered to that pillar (legacy path). */
  const pillarMemories = useMemo(() => {
    if (!drillPillar) return [];
    const dateVal = (m) => {
      const t = m.created_at || m.uploaded_at || m.timestamp;
      const ms = t ? Date.parse(t) : 0;
      return Number.isFinite(ms) ? ms : 0;
    };
    if (drillPillar === '__all__') {
      return [...memories].sort((a, b) => dateVal(b) - dateVal(a));
    }
    return memories
      .filter((m) => (m.category || '').toLowerCase() === drillPillar)
      .sort((a, b) => dateVal(b) - dateVal(a));
  }, [memories, drillPillar]);
  const pillarTotal = pillarMemories.length;
  /* Counts per pillar — used to show "12 essences" on each master card. */
  const pillarCounts = useMemo(() => {
    const counts = {};
    PILLAR_ORDER.forEach((p) => { counts[p] = 0; });
    memories.forEach((m) => {
      const c = (m.category || '').toLowerCase();
      if (counts[c] !== undefined) counts[c] += 1;
    });
    return counts;
  }, [memories]);

  /* Synthetic memory used as the MASTER CARD for a pillar — shows the
     user's profile picture inside the pillar-themed essence card frame. */
  const buildMasterMemory = useCallback((pillarId) => ({
    id: `master-${pillarId}`,
    category: pillarId,
    image_url: hiResAvatar(user?.profile_picture) || null,
    note: `${(CATEGORY_META[pillarId] || {}).label || pillarId} Master`,
    created_at: user?.created_at || new Date().toISOString(),
  }), [user]);

  const turn = (delta) => {
    setDirection(delta > 0 ? 1 : -1);
    setActiveIdx((i) => Math.max(0, Math.min(pillarTotal - 1, i + delta)));
  };

  /* Reset swiper to first card whenever the drilled pillar changes. */
  useEffect(() => { setActiveIdx(0); setDirection(1); }, [drillPillar]);

  /* Swipe support */
  const touchStartX = useRef(null);
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const sx = touchStartX.current; touchStartX.current = null;
    if (sx == null) return;
    const dx = e.changedTouches[0].clientX - sx;
    if (Math.abs(dx) < 60) return;
    turn(dx < 0 ? 1 : -1);
  };

  const activeCard = useMemo(
    () => pillarMemories[activeIdx],
    [pillarMemories, activeIdx]
  );

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[300] flex flex-col imperium-shell"
      style={{
        background:
          'radial-gradient(ellipse at top, #161108 0%, #08060a 60%, #000 100%)',
      }}
      data-testid="essence-vault">

      {/* ── Chroma-key SVG filter for footer category icons ── */}
      <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }} aria-hidden>
        <defs>
          <filter id="chromaPunchEssence" colorInterpolationFilters="sRGB">
            <feColorMatrix type="matrix" values="
              1 0 0 0 0
              0 1 0 0 0
              0 0 1 0 0
              1 1 1 0 0"/>
            <feComponentTransfer>
              <feFuncA type="table" tableValues="0 0 1 1"/>
            </feComponentTransfer>
          </filter>
        </defs>
      </svg>

      {/* Header — title only, no count */}
      <div
        className="flex items-center justify-between px-4 py-2 flex-shrink-0"
        style={{
          paddingTop: 'calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 8px)',
        }}>
        <BackPill onClick={() => (drillPillar ? setDrillPillar(null) : onClose())} accent="#D4AF37" testId="essence-close"/>
        <h2 className="font-['Cormorant_Garamond'] text-xl font-light tracking-wide flex items-center gap-2"
          style={{ color: GOLD_HI }}>
          <Sparkles size={14} style={{ color: GOLD }}/>
          {drillPillar === '__all__'
            ? 'Essence Timeline'
            : drillPillar
              ? (CATEGORY_META[drillPillar]?.label || 'Pillar')
              : 'The Essence'}
        </h2>
        <div className="w-6" />
      </div>

      {/* Sub-tab strip — STAMP · SIGN · CARDS */}
      <div className="flex-shrink-0 flex items-center justify-center gap-2 px-4 pt-1 pb-2"
        data-testid="essence-tab-strip">
        {[
          { id: 'stamp', label: 'Stamp' },
          { id: 'sign',  label: 'Sign'  },
          { id: 'cards', label: 'Cards' },
        ].map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setTab(t.id);
                if (t.id !== 'cards') setDrillPillar(null);
              }}
              data-testid={`essence-tab-${t.id}`}
              className="px-5 py-1.5 rounded-full font-['Cinzel'] uppercase tracking-[0.24em]"
              style={{
                background: active
                  ? `linear-gradient(135deg, ${GOLD_HI}, ${GOLD})`
                  : 'rgba(255,255,255,0.04)',
                color: active ? '#0a0610' : GOLD_HI,
                border: `1px solid ${active ? GOLD_HI : GOLD + '55'}`,
                fontSize: 10,
                boxShadow: active ? `0 0 14px ${GOLD}66` : 'none',
              }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Body — CARDS tab hosts the essence master + timeline swiper */}
      {tab === 'stamp' ? (
        <StampTabContent user={user} totalUploads={total} />
      ) : tab === 'sign' ? (
        <SignTabContent user={user} updateProfile={updateProfile} />
      ) : tab !== 'cards' ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6 min-h-0 text-center"
          data-testid={`essence-tab-body-${tab}`}>
          <Sparkles size={36} style={{ color: GOLD, filter: `drop-shadow(0 0 12px ${GOLD})` }}/>
          <p className="mt-4 font-['Cormorant_Garamond'] italic"
            style={{ color: 'rgba(245,217,122,0.7)', fontSize: 18 }}>Coming soon</p>
        </div>
      ) : !drillPillar ? (
        /* ── ONE MAIN MASTER CARD — profile-driven, spans full essence
             framework. Tap to open the swiper of all uploaded memories
             sorted by upload date (newest → oldest). ── */
        <div className="flex-1 flex items-center justify-center px-6 pt-4 pb-4 min-h-0"
          data-testid="essence-master-grid">
          <button
            type="button"
            onClick={() => setDrillPillar('__all__')}
            className="relative active:scale-[0.98] transition-transform h-full flex items-center justify-center"
            style={{ maxHeight: 640, aspectRatio: '9 / 16' }}
            data-testid="essence-master-main">
            <EssenceCard
              memory={buildMasterMemory('legacy')}
              user={user}
              totalUploads={total}
              sessionMinutes={sessionMinutes}
              master
            />
          </button>
        </div>
      ) : (
        /* ── DRILLED SWIPER for the timeline ── */
        <div className="flex-1 flex items-center justify-center px-3 pt-4 pb-2 min-h-0"
          onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {pillarTotal === 0 ? (
            <div className="text-center px-8" data-testid="essence-empty">
              <Sparkles size={36} style={{ color: GOLD, margin: '0 auto', filter: `drop-shadow(0 0 12px ${GOLD})` }}/>
              <p className="mt-4 font-['Cormorant_Garamond'] italic" style={{ color: 'rgba(245,217,122,0.7)', fontSize: 16 }}>
                {drillPillar === '__all__'
                  ? 'No essence forged yet.'
                  : 'No essence in this hall yet.'}
              </p>
              <p className="mt-2 font-['Outfit'] uppercase tracking-[0.25em]" style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>
                {drillPillar === '__all__'
                  ? 'upload a memory to mint your first essence'
                  : `upload a ${CATEGORY_META[drillPillar]?.label.toLowerCase()} moment to mint one`}
              </p>
            </div>
          ) : (
            <div className="relative w-full h-full flex items-center justify-center" style={{ perspective: '1600px' }}>
              <AnimatePresence custom={direction} mode="wait">
                {activeCard && (
                  <motion.div
                    key={activeCard.id || activeIdx}
                    initial={{ opacity: 0, x: direction > 0 ? 120 : -120, rotateY: direction > 0 ? 30 : -30 }}
                    animate={{ opacity: 1, x: 0, rotateY: 0 }}
                    exit={{ opacity: 0, x: direction > 0 ? -120 : 120, rotateY: direction > 0 ? -30 : 30 }}
                    transition={{ duration: 0.45, ease: [0.6, 0.05, 0.2, 0.95] }}
                    style={{ transformStyle: 'preserve-3d' }}>
                    <EssenceCard
                      memory={activeCard}
                      user={user}
                      totalUploads={total}
                      sessionMinutes={sessionMinutes}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      )}

      {/* Pager controls — only inside drilled cards view */}
      {tab === 'cards' && drillPillar && pillarTotal > 0 && (
        <div className="flex-shrink-0 flex items-center justify-between px-6 pt-1"
          style={{ paddingBottom: 'max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 16px)', boxSizing: 'border-box' }}>
          <button
            onClick={() => turn(-1)}
            disabled={activeIdx === 0}
            className="w-12 h-12 rounded-full flex items-center justify-center disabled:opacity-25 active:scale-90 transition-transform"
            style={{
              border: `1.5px solid ${GOLD}`,
              background: 'rgba(212,175,55,0.08)',
              color: GOLD_HI,
              boxShadow: `0 0 14px ${GOLD}33`,
            }}
            data-testid="essence-prev"
            aria-label="Previous card">
            <ChevronLeft size={22}/>
          </button>
          <p className="font-['Cormorant_Garamond'] italic text-xs text-white/40">
            swipe through this hall
          </p>
          <button
            onClick={() => turn(1)}
            disabled={activeIdx >= pillarTotal - 1}
            className="w-12 h-12 rounded-full flex items-center justify-center disabled:opacity-25 active:scale-90 transition-transform"
            style={{
              border: `1.5px solid ${GOLD}`,
              background: 'rgba(212,175,55,0.08)',
              color: GOLD_HI,
              boxShadow: `0 0 14px ${GOLD}33`,
            }}
            data-testid="essence-next"
            aria-label="Next card">
            <ChevronRight size={22}/>
          </button>
        </div>
      )}
    </motion.div>
  );
};

/* ── Tile icon for the launcher (faceted gold gem / essence crystal) ── */
export const EssenceTileIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <defs>
      <linearGradient id="essenceGemG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FFF1B8"/>
        <stop offset="55%" stopColor="#F5D97A"/>
        <stop offset="100%" stopColor="#8B6B2F"/>
      </linearGradient>
    </defs>
    {/* Diamond / crystal silhouette */}
    <path d="M12 2 L20 9 L12 22 L4 9 Z"
      fill="url(#essenceGemG)" stroke="#7A5418" strokeWidth="0.6" strokeLinejoin="round"/>
    {/* Top facet lines */}
    <path d="M12 2 L8 9 L12 22" fill="none" stroke="#7A5418" strokeWidth="0.5"/>
    <path d="M12 2 L16 9 L12 22" fill="none" stroke="#7A5418" strokeWidth="0.5"/>
    <path d="M4 9 L20 9"          fill="none" stroke="#7A5418" strokeWidth="0.5"/>
    {/* Central highlight */}
    <path d="M11 4 L13 4 L12.5 6 L11.5 6 Z" fill="#FFF7DC"/>
  </svg>
);

/* ═══════════════════════════════════════════════════════════════════
   STAMP TAB — a premium postage-stamp style Bliss identity card.
   Cream/sepia background with perforated edges, thin gold inner frame,
   user profile portrait sepia-toned, "BLISS SOCIETY" heading, user's
   name, Bliss ID and a rank denomination badge.
   ═══════════════════════════════════════════════════════════════════ */
const StampTabContent = ({ user, totalUploads }) => {
  // Real display name — pulls from the profile fields set on the Empire profile tab.
  const emailPrefix = (user?.email || '').split('@')[0].replace(/[._-]+/g, ' ').trim();
  const rawName = user?.nickname || user?.name || user?.first_name || emailPrefix || 'Bliss Member';
  const name = String(rawName).toUpperCase();
  const isFallback = !user?.nickname && !user?.name && !user?.first_name && !emailPrefix;
  const memberNumber = user?.member_number;
  const blissIdLabel = memberNumber ? String(memberNumber) : '—';
  const photo = hiResAvatar(user?.avatar_url || user?.photo_url || user?.profile_picture || null);
  const rank = totalUploads >= 100 ? 'CENTURION'
             : totalUploads >= 50 ? 'SOVEREIGN'
             : totalUploads >= 25 ? 'ELDER'
             : totalUploads >= 10 ? 'DISCIPLE'
             : 'INITIATE';

  // Perforation notches — small dark circles positioned all along the 4 edges.
  // Rendered as SVG overlay on top of the cream stamp so they visually appear
  // as circular cutouts (they match the obsidian app background).
  const NOTCH_R = 4;
  const SPACING = 11;
  const buildEdgeDots = (length, along, cross) => {
    const count = Math.floor((length - SPACING) / SPACING);
    const start = (length - count * SPACING) / 2;
    return Array.from({ length: count + 1 }, (_, i) => ({
      cx: along === 'x' ? start + i * SPACING : cross,
      cy: along === 'y' ? start + i * SPACING : cross,
    }));
  };

  // Using a normalized 200x260 SVG viewBox for perforation math
  const W = 200, H = 260;
  const dots = [
    ...buildEdgeDots(W, 'x', 0).map(d => ({ ...d, cy: 0 })),
    ...buildEdgeDots(W, 'x', H).map(d => ({ ...d, cy: H })),
    ...buildEdgeDots(H, 'y', 0).map(d => ({ ...d, cx: 0 })),
    ...buildEdgeDots(H, 'y', W).map(d => ({ ...d, cx: W })),
  ];

  return (
    <div className="flex-1 min-h-0 flex items-center justify-center px-4 pt-1 pb-10"
      data-testid="essence-stamp-body">
      <div className="relative" style={{ width: 'min(100%, 340px, calc((100vh - 200px) * 0.77))', aspectRatio: `${W} / ${H}` }}>
        {/* Cream stamp body */}
        <div className="absolute inset-0 rounded-[2px]"
          style={{
            background: 'radial-gradient(ellipse at 30% 20%, #F6E9C6 0%, #E9D497 45%, #C9AA5F 100%)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.35)',
          }}>
          {/* Paper grain overlay */}
          <div className="absolute inset-0 pointer-events-none opacity-30"
            style={{
              background:
                'repeating-linear-gradient(45deg, rgba(90,60,20,0.06) 0 1px, transparent 1px 4px),' +
                'repeating-linear-gradient(-45deg, rgba(90,60,20,0.06) 0 1px, transparent 1px 4px)',
            }}/>
        </div>

        {/* Perforation SVG overlay — dark circles align with app bg giving the notched illusion */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
          {dots.map((d, i) => (
            <circle key={i} cx={d.cx} cy={d.cy} r={NOTCH_R}
              fill="#050308"/>
          ))}
        </svg>

        {/* Thin gold inner frame */}
        <div className="absolute pointer-events-none rounded-[1px]"
          style={{
            top: 16, left: 16, right: 16, bottom: 16,
            border: '1.5px solid #8B6E1A',
            boxShadow: 'inset 0 0 0 3px transparent, inset 0 0 0 4px rgba(201,170,95,0.35)',
          }}/>

        {/* Content — vertically stacked: header, photo, name, Bliss ID, footer */}
        <div className="absolute flex flex-col"
          style={{ top: 22, left: 22, right: 22, bottom: 22 }}>
          {/* Header row */}
          <div className="flex items-start justify-between mb-1">
            <p className="font-['Cinzel'] font-bold tracking-[0.3em]"
              style={{ color: '#4A3208', fontSize: 11, textShadow: '0 1px 0 rgba(255,255,255,0.4)' }}>
              BLISS
            </p>
            <p className="font-['Cormorant_Garamond'] italic"
              style={{ color: '#8B6E1A', fontSize: 10 }}>
              Est. Society
            </p>
          </div>

          {/* Portrait — takes middle 55% */}
          <div className="relative flex items-center justify-center overflow-hidden"
            style={{ flex: '55 1 0%', minHeight: 0 }}>
            {photo ? (
              <img src={photo} alt={name}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="essence-stamp-photo"
                style={{
                  filter: 'sepia(0.85) contrast(1.05) saturate(0.9) brightness(0.95)',
                  mixBlendMode: 'multiply',
                }}/>
            ) : (
              <div className="rounded-full flex items-center justify-center"
                style={{
                  width: '55%', aspectRatio: '1 / 1',
                  background: 'linear-gradient(135deg, #C9AA5F, #8B6E1A)',
                  border: '2px solid #6B4A15',
                }}>
                <span className="font-['Cinzel'] font-bold"
                  style={{ color: '#4A3208', fontSize: '3.2rem' }}>
                  {name.charAt(0)}
                </span>
              </div>
            )}
          </div>

          {/* Info block — bottom */}
          <div className="mt-2 flex flex-col items-center text-center"
            style={{ flex: '0 0 auto' }}>
            <p className="font-['Cinzel'] font-bold tracking-[0.24em]"
              style={{ color: '#2A1B04', fontSize: 15, lineHeight: 1.15,
                textShadow: '0 1px 0 rgba(255,255,255,0.4)' }}
              data-testid="essence-stamp-name">
              {name}
            </p>
            {!isFallback && (
              <p className="font-['Outfit'] uppercase tracking-[0.32em] mt-0.5"
                style={{ color: '#6B4A15', fontSize: 9 }}>
                Bliss Member
              </p>
            )}
            <p className="font-['Outfit'] uppercase tracking-[0.2em] mt-1.5"
              style={{ color: '#6B4A15', fontSize: 10 }}
              data-testid="essence-stamp-id">
              Bliss ID · <span style={{ color: '#3A2606', fontWeight: 700 }}>{blissIdLabel}</span>
              <span style={{ color: '#8B6E1A', margin: '0 6px' }}>·</span>
              {rank}
            </p>
            <p className="font-['Cinzel'] tracking-[0.32em] mt-1.5 w-full"
              style={{ color: '#8B6E1A', fontSize: 9,
                borderTop: '1px solid rgba(139,110,26,0.4)', paddingTop: 4 }}>
              — BLISS SOCIETY —
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   SIGNATURE LOOP CANVAS — continuously replays the last sealed
   signature stroke-by-stroke as if the pen is redrawing it live.
   Uses stored stroke coordinates when available; falls back to a
   static image if only the PNG data URL exists.
   ═══════════════════════════════════════════════════════════════════ */
const SignatureLoopCanvas = ({ strokes, fallback }) => {
  const canvasRef = React.useRef(null);
  const wrapRef = React.useRef(null);
  const rafRef = React.useRef(0);
  const startRef = React.useRef(0);

  // Guard: no stroke data → static fallback image with subtle glow
  const hasStrokeData = strokes && Array.isArray(strokes.strokes) && strokes.strokes.length > 0
    && Number.isFinite(strokes.width) && Number.isFinite(strokes.height);

  React.useEffect(() => {
    if (!hasStrokeData) return undefined;
    const cv = canvasRef.current;
    const wrap = wrapRef.current;
    if (!cv || !wrap) return undefined;

    const fitCanvas = () => {
      const rect = wrap.getBoundingClientRect();
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      cv.width  = Math.floor(rect.width  * dpr);
      cv.height = Math.floor(rect.height * dpr);
      cv.style.width  = rect.width  + 'px';
      cv.style.height = rect.height + 'px';
      const ctx = cv.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { rect, ctx };
    };

    // Flatten strokes into total-length parameterized by cumulative distance
    // so replay speed feels natural regardless of original timing.
    const flat = [];
    let cumulative = 0;
    strokes.strokes.forEach((stroke, sIdx) => {
      for (let i = 1; i < stroke.length; i++) {
        const a = stroke[i - 1];
        const b = stroke[i];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const d = Math.hypot(dx, dy);
        cumulative += d;
        flat.push({ sIdx, a, b, at: cumulative });
      }
    });
    const total = cumulative || 1;
    const CYCLE_MS = 2600;   // full pen-replay per loop
    const HOLD_MS  = 900;    // hold the completed signature between loops

    const draw = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const t = (ts - startRef.current) % (CYCLE_MS + HOLD_MS);
      const { rect, ctx } = fitCanvas();
      ctx.clearRect(0, 0, rect.width, rect.height);
      // Scale drawing coords to canvas
      const scaleX = rect.width  / strokes.width;
      const scaleY = rect.height / strokes.height;
      const scale  = Math.min(scaleX, scaleY) * 0.9; // 10% inset
      const offX   = (rect.width  - strokes.width  * scale) / 2;
      const offY   = (rect.height - strokes.height * scale) / 2;

      const progress = Math.min(1, t / CYCLE_MS);
      const cutoff = total * progress;

      // Outer glow pass
      ctx.strokeStyle = 'rgba(233,199,122,0.9)';
      ctx.lineWidth = 3.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowColor = 'rgba(233,199,122,0.85)';
      ctx.shadowBlur = 14;
      ctx.beginPath();
      let strokeStarted = -1;
      for (const seg of flat) {
        if (seg.at > cutoff) break;
        if (seg.sIdx !== strokeStarted) {
          ctx.moveTo(offX + seg.a.x * scale, offY + seg.a.y * scale);
          strokeStarted = seg.sIdx;
        }
        ctx.lineTo(offX + seg.b.x * scale, offY + seg.b.y * scale);
      }
      ctx.stroke();

      // Bright inner core pass
      ctx.strokeStyle = '#FFF3C9';
      ctx.lineWidth = 1.6;
      ctx.shadowBlur = 6;
      ctx.beginPath();
      strokeStarted = -1;
      for (const seg of flat) {
        if (seg.at > cutoff) break;
        if (seg.sIdx !== strokeStarted) {
          ctx.moveTo(offX + seg.a.x * scale, offY + seg.a.y * scale);
          strokeStarted = seg.sIdx;
        }
        ctx.lineTo(offX + seg.b.x * scale, offY + seg.b.y * scale);
      }
      ctx.stroke();

      // Pen-tip dot at the leading edge while drawing
      if (progress < 1) {
        const tip = [...flat].reverse().find((s) => s.at <= cutoff);
        if (tip) {
          const tx = offX + tip.b.x * scale;
          const ty = offY + tip.b.y * scale;
          ctx.fillStyle = '#FFF7DC';
          ctx.shadowColor = '#FFE58A';
          ctx.shadowBlur = 18;
          ctx.beginPath();
          ctx.arc(tx, ty, 3.6, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);

    const onResize = () => { fitCanvas(); };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      startRef.current = 0;
    };
  }, [hasStrokeData, strokes]);

  if (!hasStrokeData) {
    return (
      <>
        <img src={fallback} alt="Last signature"
          className="max-h-full max-w-[90%] object-contain"
          style={{ filter: `drop-shadow(0 0 6px ${GOLD}88)` }}/>
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse at 50% 50%, ${GOLD}22 0%, transparent 60%)` }}
          animate={{ opacity: [0.15, 0.55, 0.15] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}/>
      </>
    );
  }

  return (
    <div ref={wrapRef} className="absolute inset-0" data-testid="essence-sign-loop">
      <canvas ref={canvasRef}/>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   SIGN TAB CONTENT — 75% signature pad on top, 25% last-signature
   preview on the bottom with a subtle golden "loop" pulse animation.
   Uses the SAME SignaturePad + profile save flow as TheSignature.
   ═══════════════════════════════════════════════════════════════════ */
const SignTabContent = ({ user, updateProfile }) => {
  const [signatureUrl, setSignatureUrl] = React.useState(user?.signature_data_url || null);
  const [signatureStrokes, setSignatureStrokes] = React.useState(user?.signature_strokes || null);
  const [saving, setSaving] = React.useState(false);
  const [feedback, setFeedback] = React.useState('');
  const lastSignature = user?.signature_data_url;

  const handleSeal = async () => {
    if (!signatureUrl) { setFeedback('Sign your name first'); return; }
    setSaving(true); setFeedback('');
    try {
      await updateProfile({ signature_data_url: signatureUrl, signature_strokes: signatureStrokes });
      setFeedback('Sealed');
      setTimeout(() => setFeedback(''), 1600);
    } catch (_) {
      setFeedback('Save failed — try again');
    } finally { setSaving(false); }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="essence-sign-body">
      {/* ─── Top 75% — Sign pad ─── */}
      <div className="flex flex-col items-center justify-center px-5 pt-2 pb-2"
        style={{ flex: '3 1 0%', minHeight: 0 }}>
        <p className="text-center font-['Cormorant_Garamond'] italic mb-1"
          style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15 }}>
          Sign to enter your Digital Identity
        </p>
        <p className="text-center font-['Outfit'] uppercase tracking-[0.2em] mb-3"
          style={{ color: 'rgba(212,175,55,0.4)', fontSize: 8 }}>
          A signature is your seal — no one wears your soul
        </p>
        <div className="w-full max-w-md">
          <SignaturePad
            height={200}
            onChange={(url, meta) => { setSignatureUrl(url); setSignatureStrokes(meta || null); }}
            placeholder="Sign your name"
            testId="essence-sign-pad"
          />
        </div>
        <button
          onClick={handleSeal}
          disabled={saving || !signatureUrl}
          data-testid="essence-sign-seal"
          className="w-full max-w-md mt-3 py-2.5 rounded-full font-['Outfit'] font-semibold disabled:opacity-30"
          style={{
            background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD})`,
            color: '#0a0610',
            boxShadow: signatureUrl && !saving ? `0 0 20px ${GOLD}55` : 'none',
            fontSize: 13,
          }}>
          {saving ? 'Sealing…' : 'Enter My Identity'}
        </button>
        {feedback && (
          <p className="mt-2 font-['Outfit'] uppercase tracking-[0.2em]"
            style={{ color: feedback === 'Sealed' ? '#8BE07A' : '#FF9E9E', fontSize: 9 }}>
            {feedback}
          </p>
        )}
      </div>

      {/* ─── Bottom 25% — Last signature with looping glow pulse ─── */}
      <div className="flex flex-col items-center justify-center px-4 pt-2 border-t"
        style={{ flex: '1 1 0%', minHeight: 0, borderColor: `${GOLD}22`,
          paddingBottom: 'max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 10px)' }}
        data-testid="essence-sign-last">
        <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-1.5"
          style={{ color: `${GOLD}AA`, fontSize: 8 }}>
          Last Sealed Signature
        </p>
        <div className="relative w-full max-w-md rounded-lg overflow-hidden flex items-center justify-center"
          style={{
            flex: '1 1 0%', minHeight: 0,
            background: 'linear-gradient(180deg, rgba(20,15,30,0.9), rgba(6,4,10,0.98))',
            border: `1px solid ${GOLD}33`,
          }}>
          {lastSignature ? (
            <SignatureLoopCanvas strokes={user?.signature_strokes} fallback={lastSignature} />
          ) : (
            <p className="font-['Cormorant_Garamond'] italic"
              style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>
              — no sealed signature yet —
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EssenceVault;
